import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { ResourceForm, useResource } from "@/components/ResourcePage";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { EVENT_STATUSES, fmtDate, fmtIDR, daysUntil } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/hooks/useEventOptions";
import { remove, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MapPin, CalendarDays, Search, Zap, Trash2 } from "lucide-react";

const COVERS = [
  "https://images.unsplash.com/photo-1768586471676-6af1d219e99e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];
const coverSrc = (c, fallback) => (!c ? fallback : (String(c).startsWith("http") ? c : fileUrl(c)));

export default function Events() {
  const { can } = useAuth();
  const { items, loading, reload } = useResource("events");
  const clientOptions = useOptions("clients", (c) => `${c.groom} & ${c.bride}`);
  const pkgOptions = useOptions("packages", (p) => p.name);
  const [formOpen, setFormOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = items.filter((e) => `${e.groom} ${e.bride} ${e.venue}`.toLowerCase().includes(q.toLowerCase()));

  const fields = [
    { name: "client_id", label: "Client", type: "select", options: clientOptions, full: true },
    { name: "groom", label: "Pengantin Pria", required: true },
    { name: "bride", label: "Pengantin Wanita", required: true },
    { name: "wedding_date", label: "Tanggal Wedding", type: "date", required: true },
    { name: "venue", label: "Venue" },
    { name: "package", label: "Paket", type: "select", options: pkgOptions.map((p) => ({ value: p.label, label: p.label })) },
    { name: "total_price", label: "Total Harga", type: "number" },
    { name: "status", label: "Status", type: "select", options: EVENT_STATUSES, default: "Booked" },
    { name: "pic", label: "PIC" },
    { name: "progress", label: "Progress (%)", type: "number", default: 0 },
    { name: "notes", label: "Catatan", type: "textarea", full: true },
    { name: "cover", label: "Poster / Foto Event", type: "file", full: true },
  ];

  const delEvent = async (e, ev) => {
    ev.preventDefault(); ev.stopPropagation();
    if (!window.confirm(`Hapus event ${e.groom} & ${e.bride}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try { await remove("events", e.id); toast.success("Event dihapus"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus event"); }
  };

  return (
    <div>
      <PageHeader overline="Pusat Operasional" title="Events" subtitle="Semua wedding yang Anda kelola. Klik untuk detail lengkap.">
        {can(3) && <Button onClick={() => setFormOpen(true)} data-testid="events-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Event Baru</Button>}
      </PageHeader>

      <div className="relative max-w-xs mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A7A]" />
        <Input placeholder="Cari event..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full bg-white" data-testid="events-search" />
      </div>

      {loading ? <p className="text-[#7A7A7A]">Memuat...</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 stagger">
          {filtered.map((e, i) => {
            const cd = daysUntil(e.wedding_date);
            return (
              <Link to={`/events/${e.id}`} key={e.id} className="card-lux hover-lift overflow-hidden group" data-testid="event-card">
                <div className="relative h-40 overflow-hidden">
                  <img src={coverSrc(e.cover, COVERS[i % COVERS.length])} alt="Wedding cover" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: e.cover_position || "center" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-burgundy-dark/70 to-transparent" />
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    <StatusBadge status={e.status} />
                    {can(4) && <button onClick={(ev) => delEvent(e, ev)} title="Hapus event" data-testid="event-delete-btn" className="w-7 h-7 rounded-full bg-black/40 hover:bg-red-600 text-white flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold-light">The Wedding of</p>
                    <h3 className="font-heading text-2xl leading-tight">{e.groom} & {e.bride}</h3>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#7A7A7A] mb-3">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(e.wedding_date)}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.venue || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-[#1A1A1A]">{fmtIDR(e.total_price)}</span>
                    {cd != null && cd >= 0 && e.status !== "Completed" && <span className="text-xs font-semibold text-burgundy flex items-center gap-1"><Zap className="w-3 h-3" />{cd} hari lagi</span>}
                  </div>
                  <Progress value={e.progress || 0} className="h-1.5 bg-beige" />
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && <div className="card-lux p-10 text-center text-[#7A7A7A] md:col-span-2 xl:col-span-3">Belum ada event.</div>}
        </div>
      )}

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={null} resource="events" onSaved={reload} title="Event Baru" />
    </div>
  );
}
